import React, { RefObject, useCallback, useRef, useState } from 'react';
import cn from 'classnames';

import type { BBCommonProps } from 'types/story';
import { COMPONENT_STATES } from 'common/constants';
import { UNIFORM_PROPS } from 'common/constants/component-props';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import Loader from 'common/components/Loader';
import { useClientStoryDictionary } from 'common/i18n/hooks/useClientStoryDictionary';

import { ErrorMessage } from '../Error';
import { withField, WithFieldT } from '../utils';
import { FIELD_DATA_ATTR, FieldBBType } from '../constants';

import css from './FileUploadField.scss';

type Props = BBCommonProps & WithFieldT;

const UploadIcon = () => (
	<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
		<polyline points="17 8 12 3 7 8" />
		<line x1="12" y1="3" x2="12" y2="15" />
	</svg>
);

const RemoveIcon = () => (
	<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
		<line x1="18" y1="6" x2="6" y2="18" />
		<line x1="6" y1="6" x2="18" y2="18" />
	</svg>
);

function parseAccept(allowStr: string | undefined): string | undefined {
	if (!allowStr || typeof allowStr !== 'string') return undefined;
	const trimmed = allowStr.trim();
	if (!trimmed) return undefined;
	return trimmed
		.split(/[,;\s]+/)
		.map(ext => (ext.startsWith('.') ? ext : `.${ext}`))
		.join(',');
}

type UploadedFile = { name: string; size: number; placeholderId: string };

const FileUploadField: React.FC<Props> = props => {
	const { dictionary } = useClientStoryDictionary();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
	const { uiConfig, stateAttrs, eventListeners, containerRef, isEditableMode, formContext } = props;
	const { name } = uiConfig.editorProps;
	const { fieldPlaceholder, fileUploadAllowedTypes = '' } = uiConfig.componentProps;
	const accept = parseAccept(fileUploadAllowedTypes);
	const isUploading = Boolean(formContext?.isUploadingFiles);
	const nodeProps = {
		...props.uiConfig.nodeProps,
		...(props.editableModeProps?.nodeProps ?? {}),
		className: cn(
			css.fileUploadField,
			props.uiConfig.nodeProps?.className,
			props.editableModeProps?.nodeProps?.className
		),
	};

	const isIncorrect = 'data-incorrect-state' in nodeProps || props.states[COMPONENT_STATES.INCORRECT];
	const errorMessage = props.getErrorMsg(nodeProps);

	const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];

		if (!file) return;

		const placeholderId = `placeholder-${Date.now()}-${file.name}`;
		setUploadedFile({ name: file.name, size: file.size, placeholderId });
	}, []);

	const handleRemove = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (fileInputRef.current) fileInputRef.current.value = '';

		setUploadedFile(null);
	}, []);

	const handleTriggerClick = useCallback(() => {
		if (isEditableMode) return;
		if (uploadedFile) return;
		fileInputRef.current?.click();
	}, [isEditableMode, uploadedFile]);

	const renderContent = () => {
		if (isUploading) {
			return (
				<>
					<Loader size="small" className={css.spinner} color="currentColor" />
					<span className={cn(css.uploadingText, css.textOverflow)}>
						{dictionary?.FILE_UPLOAD_FIELD?.uploading ?? '...'}
					</span>
				</>
			);
		}

		if (uploadedFile) {
			return (
				<>
					<span className={cn(css.fileName, css.textOverflow)} title={uploadedFile.name}>
						{uploadedFile.name}
					</span>
					<button
						type="button"
						className={css.removeBtn}
						onClick={handleRemove}
						disabled={isEditableMode}
						aria-label="Remove file"
					>
						<RemoveIcon />
					</button>
				</>
			);
		}

		return (
			<button className={css.uploadTrigger} type="button" onClick={handleTriggerClick} aria-label="Upload file">
				<span className={css.triggerLabel}>
					<span className={css.uploadIcon}>
						<UploadIcon />
					</span>
					{fieldPlaceholder && <span className={css.textOverflow}>{fieldPlaceholder}</span>}
				</span>
			</button>
		);
	};

	return (
		<div {...stateAttrs} {...eventListeners} {...nodeProps} ref={containerRef as RefObject<HTMLDivElement>}>
			{!isEditableMode && (
				<>
					<input
						ref={fileInputRef}
						type="file"
						name={name}
						{...{ [FIELD_DATA_ATTR]: '' }}
						data-field-type={FieldBBType.file}
						accept={accept}
						className={css.fileInput}
						onChange={handleFileChange}
						tabIndex={-1}
						aria-hidden
					/>
				</>
			)}

			<div className={css.inner}>{renderContent()}</div>

			<ErrorMessage
				isHidden={!isIncorrect || !errorMessage}
				path={props.editableModeProps?.nodeProps?.['data-path']}
				isEditableMode={!!isEditableMode}
				errorType={uiConfig.editorProps[UNIFORM_PROPS.fieldErrorShow]}
				text={errorMessage}
				editorMode={props.editorMode}
			/>
		</div>
	);
};

export default withCardTransitionContext(withField(FileUploadField));
